import strapiClient from '../strapi/config';

export const strapiAuthService = {
    // Connexion utilisateur
    login: async (identifier, password) => {
        try {
            const response = await strapiClient.post('/api/auth/login', {
                identifier, // Email ou username
                password
            });

            const { jwt, user } = response.data;

            // Stocker le token JWT
            localStorage.setItem('strapiToken', jwt);
            localStorage.setItem('strapiUser', JSON.stringify(user));

            return { token: jwt, user };
        } catch (error) {
            throw new Error(error.response?.data?.error?.message || 'Erreur de connexion');
        }
    },

    // Inscription utilisateur
    register: async (userData) => {
        try {
            const response = await strapiClient.post('/api/auth/local/register', {
                username: userData.username,
                email: userData.email,
                password: userData.password,
                // Ajouter les champs personnalisés si configurés dans Strapi
                firstname: userData.firstname,
                lastname: userData.lastname
            });

            const { jwt, user } = response.data;

            // Stocker le token JWT
            localStorage.setItem('strapiToken', jwt);
            localStorage.setItem('strapiUser', JSON.stringify(user));

            return { token: jwt, user };
        } catch (error) {
            throw new Error(error.response?.data?.error?.message || 'Erreur lors de l\'inscription');
        }
    },

    // Récupération du profil utilisateur
    getProfile: async () => {
        try {
            const token = localStorage.getItem('strapiToken');

            if (!token) {
                throw new Error('Token non trouvé');
            }

            // ✅ Exécuter les 2 requêtes EN PARALLÈLE avec Promise.all
            const [profileResponse, trainingsResponse] = await Promise.all([
                strapiClient.get('/api/users/me?populate=picture', {
                    headers: { Authorization: `Bearer ${token}` }
                }),
                strapiClient.get('/api/trainings/my-trainings', {
                    headers: { Authorization: `Bearer ${token}` }
                }).catch(() => ({ data: { data: [] } }))  // Si erreur, continuer avec []
            ]);

            const userProfile = profileResponse.data;
            userProfile.trainings = trainingsResponse.data.data || [];

            return userProfile;
        } catch (error) {
            throw new Error(error.response?.data?.error?.message || 'Erreur lors de la récupération du profil');
        }
    },

    // Mise à jour du profil
    updateProfile: async (userData) => {
        try {
            const token = localStorage.getItem('strapiToken');
            const user = JSON.parse(localStorage.getItem('strapiUser'));
            
            if (!token || !user) {
                throw new Error('Utilisateur non authentifié');
            }

            const response = await strapiClient.put(`/api/user/profile`, JSON.stringify(userData), {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });

            // Mettre à jour les données utilisateur stockées
            localStorage.setItem('strapiUser', JSON.stringify(response.data.user));

            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.error?.message || 'Erreur lors de la mise à jour du profil');
        }
    },

    // Changement de mot de passe
    changePassword: async (pwdData) => {
        try {
            const token = localStorage.getItem('strapiToken');
            const user = JSON.parse(localStorage.getItem('strapiUser'));
            
            if (!token || !user) {
                throw new Error('Utilisateur non authentifié');
            }

            const response = await strapiClient.put('/api/user/change-password', JSON.stringify(pwdData), {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });

            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.error?.message || 'Erreur lors du changement de mot de passe');
        }
    },

    // Mot de passe oublié
    forgotPassword: async (email) => {
        try {
            const response = await strapiClient.post('/api/auth/forgot-password', {
                email
            });

            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.error?.message || 'Erreur lors de l\'envoi de l\'email');
        }
    },

    // Réinitialisation du mot de passe
    resetPassword: async (pwdData) => {
        try {
            const response = await strapiClient.post('/api/auth/reset-password', JSON.stringify(pwdData));

            const { jwt, user } = response.data;

            // Stocker le nouveau token
            localStorage.setItem('strapiToken', jwt);
            localStorage.setItem('strapiUser', JSON.stringify(user));

            return { token: jwt, user };
        } catch (error) {
            throw new Error(error.response?.data?.error?.message || 'Erreur lors de la réinitialisation');
        }
    },

    // Déconnexion
    logout: () => {
        localStorage.removeItem('strapiToken');
        localStorage.removeItem('strapiUser');
    },

    // Vérifier si l'utilisateur est connecté
    isAuthenticated: () => {
        const token = localStorage.getItem('strapiToken');
        const user = localStorage.getItem('strapiUser');
        return !!(token && user);
    },

    // Obtenir l'utilisateur actuel
    getCurrentUser: () => {
        const user = localStorage.getItem('strapiUser');
        return user ? JSON.parse(user) : null;
    },

    // Obtenir le token actuel
    getToken: () => {
        return localStorage.getItem('strapiToken');
    },
};

/**
 * Vérifier la validité d'un token d'activation
 */
export const verifyActivationToken = async (token) => {
    try {
        const response = await strapiClient.post('/api/auth/verify-activation-token', {
            token: token
        });

        return response.data;
    } catch (error) {
        console.error('Erreur lors de la vérification du token:', error);
        throw new Error(
            error.response?.data?.error?.message || 
            'Erreur lors de la vérification du token'
        );
    }
};

/**
 * Activer un compte avec le token et créer le mot de passe
 */
export const activateAccount = async (token, password) => {
    try {
        const response = await strapiClient.post('/api/auth/activate-account', {
            token: token,
            password: password
        });

        const { jwt, user } = response.data;

        // Stocker le token JWT et l'utilisateur
        localStorage.setItem('strapiToken', jwt);
        localStorage.setItem('strapiUser', JSON.stringify(user));

        return { success: true, jwt, user };
    } catch (error) {
        console.error('Erreur lors de l\'activation du compte:', error);
        throw new Error(
            error.response?.data?.error?.message || 
            'Erreur lors de l\'activation du compte'
        );
    }
};